<template>
  <div class="sui-textarea">
    <div v-if="label" class="label">{{ label }}</div>
    <div ref="textAreaContainer" class="text-area" :class="text_area_classes" :tabindex="disabled ? -1 : 0" :disabled="disabled" @focus="onTextAreaContainerFocus">
      <textarea
        ref="textArea"
        class="text-area__controll sui-scroll" 
        :placeholder="placeholder" 
        :value="modelValue"
        :disabled="disabled"
        :maxlength="limitTextInput ? maxLength : undefined"
        @input="onTextAreaInput" 
        @focus="onTextAreaFocus"
        @blur="onTextAreaBlure"
        @keydown="onTextAreaKeydown"></textarea>
      <div class="text-area__counter counter"> 
        <span class="counter__count" :class="counter_classes">{{ count }}</span> 
        <span class="counter__max-length">&nbsp;/{{ maxLength }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed, toRefs, watch } from 'vue'

const textArea = ref<HTMLElement>()
const textAreaContainer = ref<HTMLElement>()
const focused = ref<boolean>(false)


const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: ''
  },
  maxLength: {
    type: Number,
    default: 3000,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  hasErrors: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: ''
  },
  maxLines: {
    type: Number,
    default: 18
  },
  limitTextInput: {
    type: Boolean,
    default: false
  }
})


const { modelValue, maxLength, disabled, label } = toRefs(props)

watch(modelValue, (newValue) => {
  if (textArea.value && newValue === '') {
    textArea.value.style.height = 'auto';
  }
});

const emit = defineEmits(['update:modelValue', 'focus', 'blur', 'enter'])

const text_area_classes = computed(() => {
  return {
    'text-area--focused': focused.value,
    'text-area--disabled': disabled.value,
    'text-area--greather': count.value < 0,
    'text-area--has-value': modelValue.value.length,
  }
})

const counter_classes = computed(() => {
  return {
    'counter__count--greather': count.value < 0
  }
})

const count  = computed(() => {
  return modelValue.value.length <= maxLength.value ? modelValue.value.length : (maxLength.value - modelValue.value.length)
})


function onTextAreaContainerFocus() {
  (textArea?.value as HTMLElement).focus()
}

function onTextAreaInput(event: Event): void {
  if (textArea.value) {
    textArea.value.style.height = 'auto'
    textArea.value.style.height = textArea.value.scrollHeight + 'px';
    textAreaContainer?.value?.scrollTo(0, textAreaContainer.value.scrollHeight)    
  }  
  emit('update:modelValue', (event.target as HTMLInputElement).value)
}

function onTextAreaFocus() {
  focused.value = true
}

function onTextAreaBlure() {
  focused.value = false
}

function onTextAreaKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter') {
    if (event.shiftKey) {
      return
    } else {
      event.preventDefault()
      emit('enter')
    }
  }
}

onMounted(() => {
  if (textArea.value) {
    textArea.value.style.height = 'auto';
    textArea.value.style.height = textArea.value.scrollHeight + 'px'
    textArea.value.style.setProperty('--line-height', '20px')
    textArea.value.style.setProperty('--max-lines', props.maxLines.toString())
  }
})
</script>

<style scoped lang="scss">

.sui-textarea {
  display: flex;
  flex-direction: column;
}

.label {
  margin-bottom: 8px;
  font-size: 20px;
  font-weight: 500;
  line-height: 28px;
}

.text-area {
  box-sizing: border-box;
  position: relative;
  padding: 15px;
  border: 1px var(--stroke-select) solid;
  border-radius: 4px;
  background-color: var(--color-bg-primary);
  max-height: 476px;
  overflow: hidden;

  &--focused, &:focus, &:focus-visible, &--has-value, &:hover {
    &:not(.text-area--disabled) {
      outline: none;
      border-color: var(--color-button-accent-hover);
    }
  }

  &--disabled {
    user-select: none;
    background-color: var(--color-bg-tertiary);
  }

  &--greather {
    background-color: var(--color-bg-quaternary);
  }

  &__controll {
    box-sizing: border-box;
    background-color: transparent;
    border: none;
    width: 100%;
    overflow: auto;
    min-height: 40px;
    max-height: calc(var(--line-height, 20px) * var(--max-lines));
    resize: none;
    margin-bottom: 4px;
    font-family: "Rubik", sans-serif;
    font-size: 14px;
    line-height: 20px;

    &:focus, &:focus-visible {
      outline: none;
    }

    &:disabled {
      background-color: transparent;
      cursor: default;
      user-select: none;
      -webkit-user-select: none;
      -webkit-touch-callout: none;
      -khtml-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      -o-user-select: none;
    }
  }

  &__counter {
    height: 16px;
    box-sizing: border-box;
    display: flex;
    justify-content: flex-end;
    width: 100%;
    font-size: 12px;
    line-height: 16px;
    color: var(--color-text-tertiary);
  }
}

.counter {  
  &__count {
    color: inherit;

    &--greather {
      color: var(--color-text-accent);
    }
  }

  &__max-length {
    color: inherit;
  }
}
</style>
